import type {
  AiKnownOperationKind,
  AiOperationalIntelligenceDomain,
  AiOperationalIntelligencePreview,
  AiOperationalRequest,
} from '../../../types/aiOperational'
import type { AiOperationalContextWindow } from '../context/operationalRetrieval'

export interface OperationalIntelligenceIntent {
  domain: AiOperationalIntelligenceDomain
  operationKind: AiKnownOperationKind
  explanation: string
}

// Scheduling verbs bypass the read-only MEETING_BRIEF classification —
// Line Execute handles them via the meeting action domain (src/services/actions/domains/meeting.ts).
const MEETING_SCHEDULING_VERB_REGEX = /\b(?:set(?:\s+up)?|book|schedule|arrange|create|make|put(?:\s+on\s+the\s+calendar)?|cancel|reschedule|move|drop)\b/i

const domainFromPrompt = (prompt: string): OperationalIntelligenceIntent | null => {
  if (/\b(report|audit summary|ledger summary|performance report|health report|export preview)\b/i.test(prompt)) {
    return {
      domain: 'REPORTS',
      operationKind: 'REPORT_PREVIEW',
      explanation: 'Report intelligence can prepare a draft preview and source checklist without persisting a generated report.',
    }
  }
  if (/\b(automation|workflow|trigger|webhook|recurring rule|auto[- ]?assign|auto[- ]?notify)\b/i.test(prompt)) {
    return {
      domain: 'AUTOMATIONS',
      operationKind: 'AUTOMATION_SUGGESTION',
      explanation: 'Automation intelligence can suggest inactive draft workflows; activation remains proposal/review based.',
    }
  }
  if (/\b(meeting|agenda|huddle|readiness sync|standup|rsvp|minutes|follow[- ]?up)\b/i.test(prompt)) {
    // Scheduling / cancellation intents are handled by Line Execute
    // adapters — do NOT return the read-only MEETING_BRIEF classification.
    if (MEETING_SCHEDULING_VERB_REGEX.test(prompt)) return null
    return {
      domain: 'MEETINGS',
      operationKind: 'MEETING_BRIEF',
      explanation: 'Meeting intelligence can prepare agenda/readiness/follow-up guidance without scheduling or mutating meetings.',
    }
  }
  return null
}

export const detectOperationalIntelligenceIntent = (request: Pick<AiOperationalRequest, 'prompt' | 'surface'>): OperationalIntelligenceIntent | null => {
  if (request.surface === 'REPORTS') return {
    domain: 'REPORTS',
    operationKind: 'REPORT_PREVIEW',
    explanation: 'Report intelligence can prepare a draft preview and source checklist without persisting a generated report.',
  }
  if (request.surface === 'AUTOMATIONS') return {
    domain: 'AUTOMATIONS',
    operationKind: 'AUTOMATION_SUGGESTION',
    explanation: 'Automation intelligence can suggest inactive draft workflows; activation remains proposal/review based.',
  }
  if (request.surface === 'MEETINGS') return {
    domain: 'MEETINGS',
    operationKind: 'MEETING_BRIEF',
    explanation: 'Meeting intelligence can prepare agenda/readiness/follow-up guidance without scheduling or mutating meetings.',
  }
  return domainFromPrompt(request.prompt)
}

// Pulse hints are stringified as full sentences ("Checklist "try 1" is
// missing its owning timeline node."). When they leak into `${scope}` the
// meeting/report brief reads as gibberish. Detect these shapes and drop
// them from the summary — the pulse still shows up in the audit trail,
// just not in the user-visible sentence.
const PULSE_MESSAGE_PATTERNS: RegExp[] = [
  /\bis missing its owning\b/i,
  /\bwithout scheduling changes\b/i,
  /\bPULSE\s+blocked=true\b/i,
  /\bBLOCKER\b/,
  /\bDEGRADED\b/,
  /^\s*Checklist\s+"/i,
]

const looksLikePulseMessage = (title: string | undefined | null): boolean => {
  if (!title) return true
  const value = String(title)
  return PULSE_MESSAGE_PATTERNS.some((pattern) => pattern.test(value))
}

const recordTitles = (context: AiOperationalContextWindow) =>
  context.primary
    .slice(0, 8)
    .map((record) => record.title)
    .filter((title): title is string => Boolean(title) && !looksLikePulseMessage(title))
    .slice(0, 4)

export const buildOperationalIntelligencePreview = (
  request: AiOperationalRequest,
  context: AiOperationalContextWindow,
): AiOperationalIntelligencePreview | null => {
  const intent = detectOperationalIntelligenceIntent(request)
  if (!intent) return null
  const titles = recordTitles(context)
  const scope = titles.length ? titles.join(', ') : 'the retrieved workspace context'
  if (intent.domain === 'REPORTS') {
    return {
      domain: 'REPORTS',
      title: 'Report preview plan',
      summary: `Prepare a draft report preview using ${scope}, recent Pulse risks, and bounded retrieval evidence.`,
      proposedSteps: [
        'Confirm report purpose and audience.',
        'Select source records from the retrieved context window.',
        'Prepare draft sections for operational health, timeline drift, blockers, and next actions.',
        'Route persistence/export through the existing Reports review and wallet flow.',
      ],
      constraints: [
        'Do not write generated_reports directly from model output.',
        'Do not charge credits for local planning/context assembly.',
        'Mask private data according to existing report sanitization rules.',
      ],
      reviewPath: 'Reports preview first; generation/export uses the existing trusted report flow.',
    }
  }
  if (intent.domain === 'AUTOMATIONS') {
    return {
      domain: 'AUTOMATIONS',
      title: 'Automation suggestion plan',
      summary: `Draft an inactive automation suggestion from ${scope} and current operational risks.`,
      proposedSteps: [
        'Identify the trigger, guard conditions, action, and owner.',
        'Simulate likely affected records and failure modes.',
        'Keep the workflow inactive until a reviewer approves it.',
        'Route activation through the existing automation proposal/review boundary.',
      ],
      constraints: [
        'Do not activate automations autonomously.',
        'Do not write automation_definitions directly from AI output.',
        'Integration and webhook actions remain Admin/reviewer gated.',
      ],
      reviewPath: 'Inactive draft suggestion only; activation requires proposal review.',
    }
  }
  return {
    domain: 'MEETINGS',
    title: 'Meeting intelligence plan',
    summary: `Prepare agenda, readiness, and follow-up guidance from ${scope} without scheduling changes.`,
    proposedSteps: [
      'Identify relevant agenda nodes and unresolved blockers.',
      'Prepare readiness talking points and decision questions.',
      'Suggest follow-up owners as draft recommendations only.',
      'Route meeting scheduling, participant edits, or transcription through existing meeting callables.',
    ],
    constraints: [
      'Do not schedule, reschedule, or edit meetings directly.',
      'Do not write meeting minutes or follow-up tasks as persistent data without review.',
      'Respect meeting participant visibility and workspace permissions.',
    ],
    reviewPath: 'Meeting brief only; schedule/agenda mutations use trusted meeting surfaces.',
  }
}
